import { useState, useEffect, useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Trophy, Golf, ChartLine, Plus, Trash, CaretDown, CaretUp, FlagCheckered } from '@phosphor-icons/react';
import { toast } from 'sonner';

function HandicapCard({ handicap }) {
  return (
    <div className="border border-border bg-card p-8 text-center" data-testid="handicap-card">
      <p className="text-xs text-muted-foreground uppercase tracking-wide font-sans font-semibold">Handicap Index</p>
      {handicap.handicap_index !== null ? (
        <p className="text-5xl font-serif font-semibold text-primary mt-2" data-testid="handicap-value">
          {handicap.handicap_index > 0 ? '+' : ''}{handicap.handicap_index}
        </p>
      ) : (
        <p className="text-2xl font-serif text-muted-foreground mt-2" data-testid="handicap-value">--</p>
      )}
      <p className="text-xs text-muted-foreground mt-2 font-sans">
        {handicap.rounds_count} round{handicap.rounds_count !== 1 ? 's' : ''} recorded
        {handicap.handicap_index === null && handicap.rounds_count < 3 && ' (need 3+)'}
      </p>
    </div>
  );
}

function RoundsList({ rounds, onDelete }) {
  if (!rounds.length) {
    return (
      <div className="text-center py-12">
        <Golf size={36} weight="thin" className="mx-auto text-muted-foreground" />
        <p className="text-muted-foreground text-sm mt-3 font-sans">No rounds recorded yet</p>
      </div>
    );
  }
  return (
    <div className="border border-border divide-y divide-border" data-testid="rounds-list">
      {rounds.map(r => {
        const diff = r.total_score - (r.par || 72);
        return (
          <div key={r.round_id} className="p-4 flex items-center justify-between" data-testid={`round-${r.round_id}`}>
            <div className="min-w-0">
              <p className="text-sm font-semibold font-sans truncate">{r.course_name}</p>
              <p className="text-xs text-muted-foreground font-sans mt-0.5">
                {new Date(r.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
              </p>
            </div>
            <div className="flex items-center gap-4 ml-4 flex-shrink-0">
              <div className="text-right">
                <p className="text-lg font-serif font-semibold">{r.total_score}</p>
                <p className={`text-xs font-sans font-medium ${diff > 0 ? 'text-destructive' : diff < 0 ? 'text-primary' : 'text-muted-foreground'}`}>
                  {diff > 0 ? `+${diff}` : diff === 0 ? 'E' : diff}
                </p>
              </div>
              <Badge variant="outline" className="rounded-sm text-xs font-sans">
                {r.score_differential}
              </Badge>
              <button
                onClick={() => onDelete(r.round_id)}
                className="p-1 text-muted-foreground hover:text-destructive transition-colors"
                data-testid={`delete-round-${r.round_id}`}
              >
                <Trash size={16} />
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function NewRoundForm({ courses, onSubmit, onCancel, onAddCourse }) {
  const [courseId, setCourseId] = useState('');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [scores, setScores] = useState(Array.from({ length: 18 }, (_, i) => ({ hole: i + 1, score: 0 })));
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showNewCourse, setShowNewCourse] = useState(false);
  const [newCourse, setNewCourse] = useState({ name: '', location: '', par: 72, slope_rating: 113, course_rating: 72 });

  const selectedCourse = courses.find(c => c.course_id === courseId);
  const courseHoles = selectedCourse?.holes || [];

  const updateScore = (idx, value) => {
    const v = parseInt(value) || 0;
    setScores(prev => prev.map((s, i) => i === idx ? { ...s, score: v } : s));
  };

  const totalScore = scores.reduce((sum, s) => sum + s.score, 0);
  const frontNine = scores.slice(0, 9).reduce((sum, s) => sum + s.score, 0);
  const backNine = scores.slice(9).reduce((sum, s) => sum + s.score, 0);

  const handleAddCourse = async () => {
    if (!newCourse.name.trim()) { toast.error('Course name required'); return; }
    try {
      const course = await onAddCourse(newCourse);
      setCourseId(course.course_id);
      setShowNewCourse(false);
      setNewCourse({ name: '', location: '', par: 72, slope_rating: 113, course_rating: 72 });
    } catch {}
  };

  const handleSubmit = async () => {
    if (!courseId) { toast.error('Select a course'); return; }
    if (scores.every(s => s.score === 0)) { toast.error('Enter your scores'); return; }
    setSubmitting(true);
    try {
      await onSubmit({ course_id: courseId, date, scores: scores.filter(s => s.score > 0), notes });
    } catch {} finally { setSubmitting(false); }
  };

  return (
    <div className="border border-border bg-card p-6 space-y-6" data-testid="new-round-form">
      <div className="flex items-center justify-between">
        <h3 className="font-serif text-xl font-medium">New Round</h3>
        <button onClick={onCancel} className="text-xs text-muted-foreground hover:text-foreground font-sans uppercase tracking-wide" data-testid="cancel-round-btn">
          Cancel
        </button>
      </div>

      {/* Course Select */}
      <div className="space-y-2">
        <label className="text-xs tracking-wide uppercase text-muted-foreground font-sans font-semibold">Course</label>
        <div className="flex gap-2">
          <select
            value={courseId}
            onChange={(e) => setCourseId(e.target.value)}
            className="flex-1 bg-transparent border-0 border-b border-border focus:border-primary focus:outline-none text-sm font-sans py-2"
            data-testid="round-course-select"
          >
            <option value="">Select a course...</option>
            {courses.map(c => (
              <option key={c.course_id} value={c.course_id}>{c.name} (Par {c.par})</option>
            ))}
          </select>
          <button
            onClick={() => setShowNewCourse(!showNewCourse)}
            className="px-3 py-1 border border-border rounded-sm text-xs font-sans text-primary hover:bg-primary/5 transition-colors"
            data-testid="add-course-toggle-btn"
          >
            <Plus size={14} weight="bold" />
          </button>
        </div>
      </div>

      {/* New Course Form */}
      {showNewCourse && (
        <div className="border border-border p-4 space-y-3 bg-muted/30 rounded-sm" data-testid="new-course-form">
          <p className="text-xs uppercase tracking-wide text-muted-foreground font-sans font-semibold">Add New Course</p>
          <input value={newCourse.name} onChange={e => setNewCourse(p => ({ ...p, name: e.target.value }))} placeholder="Course name"
            className="w-full bg-transparent border-0 border-b border-border focus:border-primary focus:outline-none focus:ring-0 px-0 py-2 text-sm font-sans" data-testid="new-course-name" />
          <input value={newCourse.location} onChange={e => setNewCourse(p => ({ ...p, location: e.target.value }))} placeholder="Location"
            className="w-full bg-transparent border-0 border-b border-border focus:border-primary focus:outline-none focus:ring-0 px-0 py-2 text-sm font-sans" data-testid="new-course-location" />
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-wide text-muted-foreground font-sans">Par</label>
              <input type="number" value={newCourse.par} onChange={e => setNewCourse(p => ({ ...p, par: parseInt(e.target.value) || 72 }))}
                className="w-full bg-transparent border-0 border-b border-border focus:border-primary focus:outline-none focus:ring-0 px-0 py-1 text-sm font-sans" data-testid="new-course-par" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wide text-muted-foreground font-sans">Slope</label>
              <input type="number" step="0.1" value={newCourse.slope_rating} onChange={e => setNewCourse(p => ({ ...p, slope_rating: parseFloat(e.target.value) || 113 }))}
                className="w-full bg-transparent border-0 border-b border-border focus:border-primary focus:outline-none focus:ring-0 px-0 py-1 text-sm font-sans" data-testid="new-course-slope" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wide text-muted-foreground font-sans">Rating</label>
              <input type="number" step="0.1" value={newCourse.course_rating} onChange={e => setNewCourse(p => ({ ...p, course_rating: parseFloat(e.target.value) || 72 }))}
                className="w-full bg-transparent border-0 border-b border-border focus:border-primary focus:outline-none focus:ring-0 px-0 py-1 text-sm font-sans" data-testid="new-course-rating" />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => setShowNewCourse(false)} className="rounded-sm">Cancel</Button>
            <Button size="sm" onClick={handleAddCourse} className="rounded-sm bg-primary" data-testid="save-new-course-btn">Add Course</Button>
          </div>
        </div>
      )}

      {/* Date */}
      <div className="space-y-2">
        <label className="text-xs tracking-wide uppercase text-muted-foreground font-sans font-semibold">Date</label>
        <input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          className="w-full bg-transparent border-0 border-b border-border focus:border-primary focus:outline-none text-sm font-sans py-2"
          data-testid="round-date-input"
        />
      </div>

      {/* Scorecard */}
      <div className="space-y-2">
        <label className="text-xs tracking-wide uppercase text-muted-foreground font-sans font-semibold">Scorecard</label>
        <div className="border border-border rounded-sm overflow-hidden">
          {/* Front 9 */}
          <div className="grid grid-cols-10 text-center text-xs font-sans bg-muted/50">
            <div className="p-2 font-semibold text-muted-foreground">Hole</div>
            {[1,2,3,4,5,6,7,8,9].map(h => <div key={h} className="p-2 font-semibold">{h}</div>)}
          </div>
          <div className="grid grid-cols-10 text-center text-xs font-sans border-t border-border">
            <div className="p-2 text-muted-foreground font-semibold">Par</div>
            {[1,2,3,4,5,6,7,8,9].map(h => {
              const hole = courseHoles.find(ch => ch.hole_number === h);
              return <div key={h} className="p-2 text-muted-foreground">{hole?.par || 4}</div>;
            })}
          </div>
          <div className="grid grid-cols-10 text-center text-xs border-t border-border">
            <div className="p-1 text-muted-foreground font-sans font-semibold flex items-center justify-center">Score</div>
            {scores.slice(0, 9).map((s, idx) => (
              <div key={idx} className="p-1">
                <input
                  type="number"
                  min="1"
                  max="15"
                  value={s.score || ''}
                  onChange={(e) => updateScore(idx, e.target.value)}
                  className="w-full text-center bg-transparent border border-border rounded-sm focus:border-primary focus:outline-none text-sm py-1 font-sans"
                  data-testid={`score-hole-${idx + 1}`}
                />
              </div>
            ))}
          </div>
          <div className="grid grid-cols-10 text-center border-t border-border bg-muted/30">
            <div className="p-2 text-xs text-muted-foreground font-sans font-semibold">OUT</div>
            <div className="p-2 text-xs font-semibold font-sans col-span-9">{frontNine || '--'}</div>
          </div>

          {/* Back 9 */}
          <div className="grid grid-cols-10 text-center text-xs font-sans bg-muted/50 border-t border-border">
            <div className="p-2 font-semibold text-muted-foreground">Hole</div>
            {[10,11,12,13,14,15,16,17,18].map(h => <div key={h} className="p-2 font-semibold">{h}</div>)}
          </div>
          <div className="grid grid-cols-10 text-center text-xs font-sans border-t border-border">
            <div className="p-2 text-muted-foreground font-semibold">Par</div>
            {[10,11,12,13,14,15,16,17,18].map(h => {
              const hole = courseHoles.find(ch => ch.hole_number === h);
              return <div key={h} className="p-2 text-muted-foreground">{hole?.par || 4}</div>;
            })}
          </div>
          <div className="grid grid-cols-10 text-center text-xs border-t border-border">
            <div className="p-1 text-muted-foreground font-sans font-semibold flex items-center justify-center">Score</div>
            {scores.slice(9).map((s, idx) => (
              <div key={idx + 9} className="p-1">
                <input
                  type="number"
                  min="1"
                  max="15"
                  value={s.score || ''}
                  onChange={(e) => updateScore(idx + 9, e.target.value)}
                  className="w-full text-center bg-transparent border border-border rounded-sm focus:border-primary focus:outline-none text-sm py-1 font-sans"
                  data-testid={`score-hole-${idx + 10}`}
                />
              </div>
            ))}
          </div>
          <div className="grid grid-cols-10 text-center border-t border-border bg-muted/30">
            <div className="p-2 text-xs text-muted-foreground font-sans font-semibold">IN</div>
            <div className="p-2 text-xs font-semibold font-sans col-span-9">{backNine || '--'}</div>
          </div>

          {/* Total */}
          <div className="border-t-2 border-primary grid grid-cols-2 text-center bg-primary/5">
            <div className="p-3 text-xs uppercase tracking-wide font-semibold font-sans text-primary">Total</div>
            <div className="p-3 text-lg font-serif font-semibold text-primary" data-testid="round-total-score">{totalScore || '--'}</div>
          </div>
        </div>
      </div>

      {/* Notes */}
      <div className="space-y-2">
        <label className="text-xs tracking-wide uppercase text-muted-foreground font-sans font-semibold">Notes</label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Round notes..."
          rows={2}
          className="w-full bg-transparent border-0 border-b border-border focus:border-primary focus:outline-none focus:ring-0 resize-none px-0 py-2 text-sm font-sans"
          data-testid="round-notes-input"
        />
      </div>

      <Button
        onClick={handleSubmit}
        disabled={submitting || !courseId}
        className="w-full h-12 rounded-sm bg-primary hover:bg-[#083313] transition-colors duration-300 text-sm tracking-wide uppercase font-semibold font-sans"
        data-testid="submit-round-btn"
      >
        {submitting ? 'Saving...' : 'Save Round'}
      </Button>
    </div>
  );
}

export default function ScoreboardPage() {
  const { user } = useAuth();
  const [handicap, setHandicap] = useState({ handicap_index: null, rounds_count: 0, recent_rounds: [] });
  const [rounds, setRounds] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewRound, setShowNewRound] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [handicapRes, roundsRes, coursesRes] = await Promise.all([
        api.get('/handicap'),
        api.get('/rounds?limit=50'),
        api.get('/courses?limit=100'),
      ]);
      setHandicap(handicapRes.data);
      setRounds(roundsRes.data.rounds);
      setCourses(coursesRes.data.courses);
    } catch (err) {
      console.error('Scorecard fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (!user) return <Navigate to="/auth" replace />;

  const handleSubmitRound = async (roundData) => {
    const { data } = await api.post('/rounds', roundData);
    setRounds(prev => [data, ...prev]);
    setShowNewRound(false);
    toast.success(`Round saved! Score: ${data.total_score} (Differential: ${data.score_differential})`);
    const handicapRes = await api.get('/handicap');
    setHandicap(handicapRes.data);
  };

  const handleDeleteRound = async (roundId) => {
    if (!window.confirm('Delete this round?')) return;
    try {
      await api.delete(`/rounds/${roundId}`);
      setRounds(prev => prev.filter(r => r.round_id !== roundId));
      toast.success('Round deleted');
      const handicapRes = await api.get('/handicap');
      setHandicap(handicapRes.data);
    } catch { toast.error('Delete failed'); }
  };

  const handleAddCourse = async (courseData) => {
    const { data } = await api.post('/courses', courseData);
    setCourses(prev => [...prev, data]);
    toast.success('Course added!');
    return data;
  };

  if (loading) {
    return (
      <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center bg-background">
        <div className="animate-pulse w-8 h-8 rounded-full bg-muted" />
      </div>
    );
  }

  const bestRound = rounds.length > 0 ? Math.min(...rounds.map(r => r.total_score)) : null;
  const avgScore = rounds.length > 0 ? Math.round(rounds.reduce((s, r) => s + r.total_score, 0) / rounds.length) : null;

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-background" data-testid="scorecard-page">
      <div className="max-w-2xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-serif text-3xl sm:text-4xl text-foreground font-medium tracking-tight" data-testid="scorecard-title">
              Scorecard
            </h1>
            <p className="text-muted-foreground text-sm mt-1 font-sans">Your GHIN Vault</p>
          </div>
          {!showNewRound && (
            <Button
              onClick={() => setShowNewRound(true)}
              className="rounded-sm bg-primary hover:bg-[#083313] gap-2 text-sm font-sans"
              data-testid="new-round-btn"
            >
              <Plus size={16} weight="bold" /> New Round
            </Button>
          )}
        </div>

        {/* Handicap + Stats */}
        <div className="grid grid-cols-3 gap-px mt-8 bg-border" data-testid="scorecard-stats">
          <HandicapCard handicap={handicap} />
          <div className="border border-border bg-card p-8 text-center">
            <p className="text-xs text-muted-foreground uppercase tracking-wide font-sans font-semibold">Best Round</p>
            <p className="text-3xl font-serif font-semibold text-foreground mt-2">{bestRound ?? '--'}</p>
          </div>
          <div className="border border-border bg-card p-8 text-center">
            <p className="text-xs text-muted-foreground uppercase tracking-wide font-sans font-semibold">Average</p>
            <p className="text-3xl font-serif font-semibold text-foreground mt-2">{avgScore ?? '--'}</p>
          </div>
        </div>

        {/* New Round Form */}
        {showNewRound && (
          <div className="mt-8">
            <NewRoundForm
              courses={courses}
              onSubmit={handleSubmitRound}
              onCancel={() => setShowNewRound(false)}
              onAddCourse={handleAddCourse}
            />
          </div>
        )}

        {/* Round History */}
        <div className="mt-8">
          <h2 className="text-xs text-muted-foreground uppercase tracking-wide font-sans font-semibold flex items-center gap-2">
            <ChartLine size={14} weight="bold" /> Round History
          </h2>
          <div className="mt-4">
            <RoundsList rounds={rounds} onDelete={handleDeleteRound} />
          </div>
        </div>
      </div>
    </div>
  );
}
